import React from 'react';
import styled from '@emotion/styled';
import SelectPopup from 'common/components/SelectPopup';
import { ICONS } from 'icons/iconsMap';
import { SelectPopupItemLabelState, SelectPopupItemProps } from 'common/types/selectMenu';
import PillButton from 'common/components/PillButton';
import useFloatingMenu from 'common/hooks/useFloatingMenu';

/**
 * EXAMPLE OF USAGE OF THIS COMPONENT
 *
 * <DropdownPillMenu
      id='markets'
      label={'Markets'}
      items={[
        { label: 'Germany', value: 'germany', icon: 'alert' },
        { label: 'Spain', value: 'spain', icon: 'annotate' },
      ]}
      icon='globe'
      onSelect={options => console.log(options)}
      multiselect
    />
 */

const DropdownContainer = styled.div`
  border-radius: ${({ theme }) => theme.v2.borderRadius}px;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: ${({ theme }) => theme.v2.colours.gray200};
  }
`;

interface Props {
  id: string;
  label?: string;
  items: SelectPopupItemProps[];
  // Return a list of selected values
  onSelect?: (options: Array<string>) => void;
  icon?: keyof typeof ICONS;
  removable?: boolean;
  onRemove?: (id: string) => void;
  showArrow?: boolean;
  multiselect?: boolean;
  isInitialyOpen?: boolean;
  removeSelectedItem?: boolean;
  isLoading?: boolean;
  renderSelectItem?: (value: string, checked: boolean, label: SelectPopupItemLabelState | string) => JSX.Element;
  popupContainerPadding?: number[];
  noHover?: boolean;
  popupWidth?: number;
  noPadding?: boolean;
  renderPopupHeader?: () => JSX.Element;
}

const DropdownPillMenu = ({
  id,
  label,
  items,
  onSelect,
  icon,
  removable,
  onRemove,
  showArrow = true,
  multiselect,
  isInitialyOpen = false,
  removeSelectedItem = false,
  isLoading,
  renderSelectItem,
  popupContainerPadding,
  noHover = false,
  noPadding = false,
  popupWidth,
  renderPopupHeader,
}: Props) => {
  const { isOpen, setIsOpen, refs, floatingStyles, getFloatingProps, getReferenceProps } = useFloatingMenu({
    popupPlacement: 'bottom-start',
    isDefaultOpen: isInitialyOpen,
  });

  return (
    <DropdownContainer data-testid={id}>
      <PillButton
        id={id}
        label={label}
        removable={removable}
        icon={icon}
        showArrow={showArrow}
        getReferenceProps={getReferenceProps}
        setReference={refs.setReference}
        onRemove={onRemove}
      />
      <SelectPopup
        items={items}
        onSelect={onSelect}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        multiselect={multiselect}
        setFloating={refs.setFloating}
        floatingStyles={floatingStyles}
        getFloatingProps={getFloatingProps}
        removeSelectedItem={removeSelectedItem}
        isLoading={isLoading}
        renderItem={renderSelectItem}
        popupContainerPadding={popupContainerPadding}
        noHover={noHover}
        noPadding={noPadding}
        popupWidth={popupWidth}
        renderPopupHeader={renderPopupHeader}
      />
    </DropdownContainer>
  );
};

export default DropdownPillMenu;
